/**
 * WHF scroll reveal. Small, framework free, loaded with defer from snippets/whf-head.liquid.
 *
 * Elements marked [data-whf-reveal="up|card|list|media"], and the element children of
 * [data-whf-reveal-group="..."], fade and rise into place once, the first time they scroll into view.
 * Timing, distance, easing, stagger and trigger match the reference build (values in assets/whf-reveal.css):
 * - up, media: IntersectionObserver threshold 0.1, rootMargin 0px 0px -50px 0px. In a group of type up
 *   (a heading stack) each child waits 100ms more than the one before it.
 * - card: threshold 0.1. Cards in the same row reveal together, 140ms apart.
 * - list: threshold 0.05. Each item waits (index % 5) x 70ms.
 * Each element is unobserved after it reveals, so it plays once.
 *
 * Only elements that start below the fold are hidden, after the first layout read, so nothing on screen at
 * load (the hero, the header) ever starts hidden. Nothing runs with reduced motion, in the theme editor or
 * without IntersectionObserver, and the hidden state needs html.whf-reveal-ready, so content stays visible
 * if this script never runs. App content (Judge.me widgets, Seal subscriptions) is never armed.
 */
(() => {
  if (
    !('IntersectionObserver' in window) ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    /** @type {any} */ (window).Shopify?.designMode
  ) {
    return;
  }

  const REFERENCE_TRIGGER = { threshold: 0.1, rootMargin: '0px 0px -50px 0px' };
  /** @type {Record<string, {duration: number, step: number, threshold: number, rootMargin?: string}>} */
  const VARIANTS = {
    up: { duration: 900, step: 100, ...REFERENCE_TRIGGER },
    media: { duration: 1000, step: 100, ...REFERENCE_TRIGGER },
    card: { duration: 700, step: 140, threshold: 0.1 },
    list: { duration: 600, step: 70, threshold: 0.05 },
  };
  // Never animated: the hero and header areas, and anything an app renders or injects
  const EXCLUDED = '.whf-header, .whf-hero, .whf-pdp, .whf-collection-hero, .whf-page-hero, .whf-blog-hero, .whf-article__hero';
  const APP =
    '.jdgm-widget:not(.jdgm-preview-badge), [id^="judgeme_"], .whf-reviews-host, [class*="sealsubs"], [class*="seal-subscription"], .shopify-app-block';
  const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK', 'INPUT']);

  /** @param {Element} el */
  const variantOf = (el) => {
    const name = el.getAttribute('data-whf-reveal') || 'up';
    return name in VARIANTS ? name : 'up';
  };

  /** @type {Record<string, IntersectionObserver>} */
  const observers = {};

  /**
   * @param {HTMLElement} el
   * @param {number} delay - ms
   */
  const show = (el, delay) => {
    const variant = variantOf(el);
    observers[variant].unobserve(el);
    el.style.setProperty('--whf-reveal-delay', `${delay}ms`);
    el.classList.add('whf-revealing');
    el.classList.remove('whf-reveal-pending');
    window.setTimeout(() => {
      el.classList.remove('whf-revealing');
      el.style.removeProperty('--whf-reveal-delay');
    }, delay + VARIANTS[variant].duration + 50);
  };

  /** @param {HTMLElement} el */
  const reveal = (el) => {
    const variant = variantOf(el);
    const siblings = el.parentElement ? [...el.parentElement.children] : [el];
    if (variant === 'card') {
      // The whole row (and rail cards scrolled off to the side) comes in together, left to right
      const row = siblings.filter(
        (sibling) =>
          sibling === el ||
          (sibling.classList.contains('whf-reveal-pending') &&
            variantOf(sibling) === 'card' &&
            sibling.getBoundingClientRect().top < window.innerHeight)
      );
      row.forEach((card, index) => show(/** @type {HTMLElement} */ (card), (index % 5) * VARIANTS.card.step));
    } else if (variant === 'list') {
      const index = siblings.filter((sibling) => sibling.getAttribute('data-whf-reveal') === 'list').indexOf(el);
      show(el, (Math.max(index, 0) % 5) * VARIANTS.list.step);
    } else {
      show(el, Number(el.dataset.whfRevealDelay) || 0);
    }
  };

  for (const [name, options] of Object.entries(VARIANTS)) {
    observers[name] = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const el = /** @type {HTMLElement} */ (entry.target);
          if (!el.classList.contains('whf-reveal-pending')) {
            observers[name].unobserve(el);
          } else if (entry.isIntersecting) {
            // An element too tall to ever reach the threshold reveals as soon as it enters
            const viewport = entry.rootBounds?.height || window.innerHeight;
            const tall = entry.boundingClientRect.height * options.threshold > viewport;
            if (entry.intersectionRatio >= options.threshold || tall) reveal(el);
          }
        }
      },
      { threshold: [0, options.threshold], rootMargin: options.rootMargin || '0px' }
    );
  }

  // Group children take the group's variant; a heading stack (up) gets the 100ms steps
  document.querySelectorAll('[data-whf-reveal-group]').forEach((group) => {
    const variant = group.getAttribute('data-whf-reveal-group') || 'up';
    let index = 0;
    for (const child of group.children) {
      if (SKIP_TAGS.has(child.tagName) || child.hasAttribute('hidden')) continue;
      if (!child.hasAttribute('data-whf-reveal')) child.setAttribute('data-whf-reveal', variant);
      if (variant === 'up' && !child.hasAttribute('data-whf-reveal-delay')) {
        child.setAttribute('data-whf-reveal-delay', String(index * VARIANTS.up.step));
      }
      index += 1;
    }
  });

  // Layout is read before any reveal style applies: only visible elements fully below the fold are armed
  const fold = window.innerHeight;
  const items = [...document.querySelectorAll('[data-whf-reveal]')].filter((el) => {
    if (el.parentElement?.closest('[data-whf-reveal]') || el.closest(EXCLUDED) || el.matches(APP) || el.querySelector(APP)) {
      return false;
    }
    const box = el.getBoundingClientRect();
    return box.height > 0 && box.top >= fold;
  });
  if (!items.length) return;

  document.documentElement.classList.add('whf-reveal-ready');
  for (const el of items) {
    el.classList.add('whf-reveal-pending');
    observers[variantOf(el)].observe(el);
  }

  // Printing shows everything
  window.addEventListener('beforeprint', () => {
    document.querySelectorAll('.whf-reveal-pending').forEach((el) => show(/** @type {HTMLElement} */ (el), 0));
  });
})();
